// src/features/merchantConfig/components/editors/editorChrome.jsx
//
// Shared chrome shared by every field editor - label/error/help-text layout,
// focus rings, dirty indicator dot. Keeping it here means the per-type
// editors stay tiny and visually consistent.

import React from "react";
import { AlertCircle, Pencil } from "lucide-react";

/**
 * Compose Tailwind classes for an input/select/textarea. Caller passes
 * `hasError` so error/non-error focus styles stay tidy.
 *
 * @param {{ hasError?: boolean, extra?: string }} opts
 */
export function inputClass({ hasError = false, extra = "" } = {}) {
  return [
    "w-full rounded-md border bg-white px-3 py-1.5 text-sm shadow-sm",
    "placeholder:text-gray-400",
    "focus:outline-none focus:ring-2",
    hasError
      ? "border-red-300 text-red-900 focus:border-red-400 focus:ring-red-100"
      : "border-gray-300 text-gray-900 focus:border-blue-400 focus:ring-blue-100",
    "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
    extra,
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Render a dirty-field indicator (small pencil + colored dot). Visually
 * cheap - does not bloat the layout.
 */
export function DirtyDot({ visible }) {
  if (!visible) return null;
  return (
    <span
      title="Unsaved changes on this field"
      className="inline-flex items-center gap-1 text-amber-600"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
      <Pencil className="h-3 w-3" aria-hidden="true" />
      <span className="sr-only">Unsaved changes on this field</span>
    </span>
  );
}

/**
 * Inline error message below an editor.
 */
export function FieldError({ error }) {
  if (!error) return null;
  return (
    <p className="mt-1 inline-flex items-start gap-1 text-xs text-red-700">
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{error}</span>
    </p>
  );
}
